/**
 * useVideoAbSlot — hook for session-stable A/B video variant selection.
 *
 * Fetches a named slot from /api/video-ab-slot?slot=<key>, picks one variant
 * deterministically per browser tab session (persisted in sessionStorage), and
 * returns the resolved playback ID plus tracking metadata.
 *
 * When the slot is disabled, has no enabled variants, or the API is unavailable,
 * returns playbackId: null so callers can fall back to their own chain.
 */

import { parseVideoAbVariants } from "@/lib/parseVideoAbVariants";
import { useEffect, useRef, useState } from "react";

export type VideoAbVariant = {
  playback_id: string;
  label: string;
  weight: number;
  enabled: boolean;
  /** ISO2 country codes this variant applies to. Empty/undefined = all countries. */
  countries?: string[];
  /** Stripe Price ID (price_…) que se cobra si gana esta variante. Vacío = hereda del país/default. */
  stripe_price_id?: string;
  /** Label del CTA VIP si gana esta variante. Vacío = hereda del país/default. */
  vip_label?: string;
  /** Precio full USD/mes para copy en landing (tachado). Vacío = hereda del país. */
  full_price_usd?: number;
  /** % off primer mes (1-100). Checkout usa cupón PROMO{N}_1M sobre el price_ full. */
  first_month_discount_percent?: number;
  /**
   * Reference to a centralized VIP Video Asset (brings playback + price + image).
   * Siempre numérico acá: `/api/video-ab-slot` normaliza con `Number()` antes de
   * responder (api/video-ab-slot.ts), igual que `api/lib/parseVideoAbVariants.ts`.
   */
  vip_video_asset_id?: number | null;
};

export type VideoAbSlotConfig = {
  slot_key: string;
  label: string;
  variants: VideoAbVariant[];
  selection_mode: "weighted_random" | "first";
  enabled: boolean;
  notes?: string | null;
  /** ISO timestamp from DB — invalidates session assignment when admin saves. */
  updated_at?: string | null;
  config_version?: string | null;
};

export type UseVideoAbSlotResult = {
  /** Resolved Mux playback ID for this session, or null if unavailable */
  playbackId: string | null;
  /** Human-readable label for the selected variant (for analytics) */
  variantLabel: string;
  /** 0-based index into allVariants, -1 if none selected */
  variantIndex: number;
  /** Stripe Price ID de la variante elegida (para el checkout), o "" si no tiene. */
  variantStripePriceId: string;
  /** Label del CTA VIP de la variante elegida, o "" si no tiene. */
  variantVipLabel: string;
  /** Precio full USD de la variante (landing), o undefined si no tiene. */
  variantFullPriceUsd: number | undefined;
  /** % off primer mes de la variante, o undefined si no tiene. */
  variantFirstMonthDiscountPercent: number | undefined;
  /** VIP Video Asset ID de la variante (para tracking y resolución centralizada) */
  variantVipVideoAssetId: number | null;
  /** All variants (enabled + disabled) from the slot config */
  allVariants: VideoAbVariant[];
  /** True once the API response has been received (even if no variants) */
  isReady: boolean;
  /** Slot enabled flag from API */
  slotEnabled?: boolean;
  /** Config version (updated_at) for experience snapshot / revalidate */
  configVersion?: string | null;
};

const SESSION_PREFIX = "video_ab:";
const FETCH_TIMEOUT_MS = 3000;

/** Weighted-random selection. Returns index in `variants` array, -1 if nothing enabled. */
function pickVariant(variants: VideoAbVariant[], mode: "weighted_random" | "first", country?: string): number {
  const countryUpper = country?.toUpperCase();
  const enabled = variants
    .map((v, i) => ({ v, i }))
    .filter(({ v }) => {
      if (!v.enabled || !v.playback_id?.trim()) return false;
      // Countries filter: empty or undefined = applies to all countries.
      if (!countryUpper || !v.countries?.length) return true;
      return v.countries.some((c) => c.toUpperCase() === countryUpper);
    });

  if (enabled.length === 0) return -1;
  if (mode === "first") return enabled[0].i;

  const totalWeight = enabled.reduce((sum, { v }) => sum + (v.weight ?? 1), 0);
  if (totalWeight <= 0) return enabled[0].i;

  let remaining = Math.random() * totalWeight;
  for (const { v, i } of enabled) {
    remaining -= v.weight ?? 1;
    if (remaining <= 0) return i;
  }
  return enabled[enabled.length - 1].i;
}

const VERSION_PREFIX = "video_ab_ver:";

function variantEligibleForCountry(v: VideoAbVariant, country?: string): boolean {
  if (!v.enabled || !String(v.playback_id || "").trim()) return false;
  const countryUpper = country?.toUpperCase();
  if (!countryUpper || !v.countries?.length) return true;
  return v.countries.some((c) => c.toUpperCase() === countryUpper);
}

/** Stable per-session assignment; re-picks when admin config_version changes. */
function getOrCreateAssignment(
  slotKey: string,
  variants: VideoAbVariant[],
  mode: "weighted_random" | "first",
  country?: string,
  configVersion?: string | null,
): number {
  // Include country in key so MX and CO users get independent assignments
  // even when using the same browser/device.
  const countryTag = country ? `:${country.toUpperCase()}` : "";
  const key = `${SESSION_PREFIX}${slotKey}${countryTag}`;
  const verKey = `${VERSION_PREFIX}${slotKey}${countryTag}`;
  try {
    const prevVer = sessionStorage.getItem(verKey);
    const ver = configVersion || "";
    if (ver && prevVer && prevVer !== ver) {
      sessionStorage.removeItem(key);
    }
    if (ver) sessionStorage.setItem(verKey, ver);

    const stored = sessionStorage.getItem(key);
    if (stored !== null) {
      const idx = parseInt(stored, 10);
      // Keep sticky assignment only if that variant is still a real, eligible option
      // (same Mux on A/B is OK — price/label/weight still differ by index).
      if (
        Number.isFinite(idx) &&
        idx >= 0 &&
        idx < variants.length &&
        variantEligibleForCountry(variants[idx], country)
      ) {
        return idx;
      }
    }
  } catch { /* sessionStorage blocked (private mode, etc.) */ }

  const idx = pickVariant(variants, mode, country);
  if (idx >= 0) {
    try { sessionStorage.setItem(key, String(idx)); } catch { /* */ }
  }
  return idx;
}

const EMPTY: UseVideoAbSlotResult = {
  playbackId: null,
  variantLabel: "",
  variantIndex: -1,
  variantStripePriceId: "",
  variantVipLabel: "",
  variantFullPriceUsd: undefined,
  variantFirstMonthDiscountPercent: undefined,
  variantVipVideoAssetId: null,
  allVariants: [],
  isReady: false,
  slotEnabled: false,
  configVersion: null,
};

function applySlot(
  slot: VideoAbSlotConfig,
  slotKey: string,
  country: string | undefined,
): UseVideoAbSlotResult {
  const variants = parseVideoAbVariants(slot.variants);
  const configVersion = slot.config_version || slot.updated_at || null;
  if (!slot.enabled || variants.length === 0) {
    return {
      ...EMPTY,
      allVariants: variants,
      isReady: true,
      slotEnabled: Boolean(slot.enabled),
      configVersion,
    };
  }

  const idx = getOrCreateAssignment(
    slotKey,
    variants,
    slot.selection_mode ?? "weighted_random",
    country,
    configVersion,
  );
  const chosen = idx >= 0 ? variants[idx] : null;

  return {
    playbackId: chosen?.playback_id?.trim() || null,
    variantLabel: chosen?.label?.trim() || "",
    variantIndex: idx,
    variantStripePriceId: chosen?.stripe_price_id?.trim() || "",
    variantVipLabel: chosen?.vip_label?.trim() || "",
    variantFullPriceUsd:
      typeof chosen?.full_price_usd === "number" && chosen.full_price_usd > 0
        ? chosen.full_price_usd
        : undefined,
    variantFirstMonthDiscountPercent:
      typeof chosen?.first_month_discount_percent === "number" &&
      chosen.first_month_discount_percent > 0 &&
      chosen.first_month_discount_percent <= 100
        ? Math.round(chosen.first_month_discount_percent)
        : undefined,
    variantVipVideoAssetId: chosen?.vip_video_asset_id ?? null,
    allVariants: variants,
    isReady: true,
    slotEnabled: true,
    configVersion,
  };
}

export function useVideoAbSlot(slotKey: string, country?: string): UseVideoAbSlotResult {
  const [result, setResult] = useState<UseVideoAbSlotResult>(EMPTY);
  const lastVersionRef = useRef<string | null>(null);

  useEffect(() => {
    if (!slotKey) return;

    let cancelled = false;
    let activeController: AbortController | null = null;
    let timedOut = false;

    const fetchSlot = () => {
      // Supersede any in-flight request (country change / focus revalidate).
      activeController?.abort();
      timedOut = false;
      const controller = new AbortController();
      activeController = controller;
      // Cache-bust + no-store so init lifts real admin variants (price/label/weight),
      // including several variants that share the same Mux playback_id.
      const q = `&_t=${Date.now()}`;
      const timeoutId = setTimeout(() => {
        timedOut = true;
        controller.abort();
      }, FETCH_TIMEOUT_MS);
      fetch(`/api/video-ab-slot?slot=${encodeURIComponent(slotKey)}${q}`, {
        signal: controller.signal,
        cache: "no-store",
      })
        .then((r) => (r.ok ? (r.json() as Promise<VideoAbSlotConfig>) : Promise.reject(r.status)))
        .then((slot: VideoAbSlotConfig) => {
          clearTimeout(timeoutId);
          if (cancelled || activeController !== controller) return;
          const next = applySlot(slot, slotKey, country);
          lastVersionRef.current = next.configVersion ?? null;
          setResult(next);
        })
        .catch(() => {
          clearTimeout(timeoutId);
          if (cancelled || activeController !== controller) return;
          // Network error or timeout: unblock landing (country dualCta / fallback).
          // Do not clear an already-successful slot from a race.
          setResult((prev) =>
            prev.isReady && prev.playbackId
              ? prev
              : { ...EMPTY, isReady: true },
          );
          void timedOut;
        });
    };

    // Init: fetch so DualCTA mounts with the configured variant (real functions).
    fetchSlot();

    const onVis = () => {
      if (document.visibilityState === "visible") fetchSlot();
    };
    document.addEventListener("visibilitychange", onVis);

    return () => {
      cancelled = true;
      activeController?.abort();
      activeController = null;
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [slotKey, country]);

  return result;
}
